// store.tsx — global UI/client state (Context + useReducer).
//
// Scope vs. the prototype: this holds ONLY client/UI concerns — the active
// role, the active store filter, and transient toasts. The prototype's reducer
// also fabricated server data (products, batches, the audit trail and movement
// ledger); that now lives behind the API and is fetched/mutated via TanStack
// Query. The client no longer invents compliance data.

import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import type { ReactNode } from 'react';
import { ROLES, PEOPLE, can as canFn } from '@/auth/rbac';
import type { RoleId } from '@/auth/rbac';
import { useRealApi } from '@/api/paths';
import type { Tone } from '@/domain/types';
import { toastBus } from '@/foundation/toastBus';

export interface Toast {
  id: number;
  msg: string;
  tone: Tone;
}

export interface UIState {
  role: RoleId; // active/display role (demo switcher + headline)
  roles: RoleId[]; // ALL roles the user holds — capabilities are the union
  store: string; // active store filter — 'ALL' or a store id
  realName: string; // from backend /me endpoint (empty until fetched)
  userId: string; // from backend /me endpoint (empty until fetched)
  avatarUrl: string; // current user's photo (SAS URL from /me); empty → initials fallback
  // Real API: false until the user's roles are known (from /me, or saved by the last /me). Until
  // then `can()` is false, so a fresh load never fires role-gated requests as the default role.
  rolesKnown: boolean;
  toasts: Toast[];
}

type Action =
  | { type: 'SET_ROLE'; role: RoleId }
  | { type: 'SET_ROLES'; roles: RoleId[]; display: RoleId }
  | { type: 'SET_STORE'; store: string }
  | { type: 'SET_NAME'; name: string }
  | { type: 'SET_USER_ID'; userId: string }
  | { type: 'SET_AVATAR'; url: string }
  | { type: 'TOAST'; toast: { msg: string; tone?: Tone } }
  | { type: 'UNTOAST'; id: number };

function reducer(state: UIState, action: Action): UIState {
  switch (action.type) {
    case 'SET_ROLE':
      // demo/QA switcher: simulate exactly one role
      return { ...state, role: action.role, roles: [action.role], rolesKnown: true };
    case 'SET_ROLES':
      // real user from /me: keep all roles (caps = union), display the best one
      return {
        ...state,
        roles: action.roles.length ? action.roles : [action.display],
        role: action.display,
        rolesKnown: true,
      };
    case 'SET_STORE':
      return { ...state, store: action.store };
    case 'SET_NAME':
      return { ...state, realName: action.name };
    case 'SET_USER_ID':
      return { ...state, userId: action.userId };
    case 'SET_AVATAR':
      return { ...state, avatarUrl: action.url };
    case 'TOAST': {
      // One toast per message: a single failure often reaches here twice (the
      // global MutationCache fallback plus a local onError with the same
      // detail) — showing the text once is enough while it's still visible.
      const tone = action.toast.tone ?? 'success';
      if (state.toasts.some((t) => t.msg === action.toast.msg && (t.tone ?? 'success') === tone))
        return state;
      return {
        ...state,
        toasts: [
          ...state.toasts,
          { id: Date.now() + Math.random(), tone: 'success', ...action.toast },
        ],
      };
    }
    case 'UNTOAST':
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.id) };
    default:
      return state;
  }
}

const ROLE_KEY = 'piq.ui.role';
const ROLES_KEY = 'piq.ui.roles';
const STORE_KEY = 'piq.ui.store';

/** Clear the persisted demo/QA display role (BUG-UI-001).
 *
 * Called on logout so a previous account's role can't bleed into the next
 * login's pre-/me render. The real session always re-derives roles from
 * /api/identity/me/ (see useMe); this only wipes the local QA-switcher value. */
export function clearPersistedUiRole(): void {
  try {
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(ROLES_KEY);
  } catch {
    /* tests / SSR */
  }
}

function initState(): UIState {
  // The active role is a demo/QA device until Entra claims are wired (see
  // auth/rbac.ts); persist it so role-based UAT survives page reloads.
  let role: RoleId = 'inventory_manager';
  let roles: RoleId[] | null = null;
  let store = 'ALL';
  let rolesKnown = !useRealApi;
  try {
    const saved = localStorage.getItem(ROLE_KEY);
    if (saved && saved in ROLES) {
      role = saved as RoleId;
      rolesKnown = true;
    }
    const savedRoles = JSON.parse(localStorage.getItem(ROLES_KEY) || 'null');
    if (Array.isArray(savedRoles) && savedRoles.length && savedRoles.every((r) => r in ROLES)) {
      roles = savedRoles as RoleId[];
    }
    // validated against the scope-filtered store list once it loads (Topbar)
    const savedStore = localStorage.getItem(STORE_KEY) || 'ALL';
    // Only accept 'ALL' or UUID-shaped values — reject mock IDs like 'ST-MAN'
    // bleeding into a real-API session. In mock mode the ids ARE mock-shaped,
    // so the guard must not apply or store selection could never persist.
    const mockMode = import.meta.env.VITE_USE_MOCKS === 'true';
    if (
      savedStore === 'ALL' ||
      mockMode ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(savedStore)
    ) {
      store = savedStore;
    }
  } catch {
    /* tests / SSR */
  }
  return {
    role,
    roles: roles ?? [role],
    store,
    realName: '',
    userId: '',
    avatarUrl: '',
    rolesKnown,
    toasts: [],
  };
}

export interface StoreValue {
  state: UIState;
  dispatch: React.Dispatch<Action>;
  role: RoleId;
  /** every role the user holds (capabilities are the union) — used for store-scope decisions */
  roles: RoleId[];
  roleName: string;
  me: string;
  readOnly: boolean;
  /** active store id ('ALL' or a store id) */
  store: string;
  /** current user's backend UUID */
  userId: string;
  /** current user's avatar URL (empty → initials fallback) */
  avatarUrl: string;
  setRole: (role: RoleId) => void;
  setRoles: (roles: RoleId[], display: RoleId) => void;
  setStore: (store: string) => void;
  setName: (name: string) => void;
  setUserId: (userId: string) => void;
  setAvatar: (url: string) => void;
  can: (cap: string) => boolean;
  toast: (msg: string, tone?: Tone) => void;
}

const Ctx = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, initState);

  // auto-dismiss the most recent toast — errors linger longer so users can
  // actually read what failed
  useEffect(() => {
    if (!state.toasts.length) return;
    const t = state.toasts[state.toasts.length - 1];
    const ttl = t.tone === 'error' ? 8000 : 4000;
    const id = setTimeout(() => dispatch({ type: 'UNTOAST', id: t.id }), ttl);
    return () => clearTimeout(id);
  }, [state.toasts]);

  // Subscribe to the global toast bus (non-React sources like MutationCache)
  useEffect(() => {
    return toastBus.subscribe((msg, tone) => {
      dispatch({ type: 'TOAST', toast: { msg, tone } });
    });
  }, []);

  const value = useMemo<StoreValue>(
    () => ({
      state,
      dispatch,
      role: state.role,
      roles: state.roles,
      roleName: ROLES[state.role].name,
      me: state.realName || PEOPLE[state.role],
      readOnly: ROLES[state.role].readOnly,
      store: state.store,
      userId: state.userId,
      avatarUrl: state.avatarUrl,
      setRole: (role) => {
        try {
          localStorage.setItem(ROLE_KEY, role);
          localStorage.removeItem(ROLES_KEY); // the switcher simulates exactly this one role
        } catch {
          /* tests / SSR */
        }
        dispatch({ type: 'SET_ROLE', role });
      },
      setRoles: (roles, display) => {
        try {
          // so the next page load starts with this user's roles, not the default
          localStorage.setItem(ROLE_KEY, display);
          localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
        } catch {
          /* tests / SSR */
        }
        dispatch({ type: 'SET_ROLES', roles, display });
      },
      setStore: (store) => {
        try {
          localStorage.setItem(STORE_KEY, store); // outside the reducer — StrictMode
        } catch {
          /* tests / SSR */
        }
        dispatch({ type: 'SET_STORE', store });
      },
      setName: (name) => dispatch({ type: 'SET_NAME', name }),
      setUserId: (userId) => dispatch({ type: 'SET_USER_ID', userId }),
      setAvatar: (url) => dispatch({ type: 'SET_AVATAR', url }),
      // capabilities are the UNION of all roles the user holds
      can: (cap) => state.rolesKnown && state.roles.some((r) => canFn(r, cap)),
      toast: (msg, tone = 'success') => dispatch({ type: 'TOAST', toast: { msg, tone } }),
    }),
    [state],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): StoreValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStore must be used within <StoreProvider>');
  return ctx;
}
